/**
 * Fetch wrapper for the gateway REST API.
 *
 * Responsibilities concentrated here so the rest of the app never touches fetch
 * directly: base URL resolution, the Authorization header, a request timeout,
 * retry with exponential backoff for idempotent reads, typed errors, and a
 * uniform unwrap of the gateway's `{ data: ... }` envelope. Balance and signing
 * paths are deliberately marked no-store so neither the HTTP cache nor the
 * service worker can serve a stale or sensitive response.
 */

import { AUTO_PROVISION_TIMEOUT_MS } from './provisionPlan';
import { config, apiUrl } from '@/config';
import { adjustInFlight } from '@/store/store';
import { reportMaintenance } from './maintenance';

/** Structured detail carried by an {@link ApiError}. */
export interface ApiErrorOptions {
  /** HTTP status, or 0 for a transport failure. */
  status?: number;
  /** Stable error code from the gateway, if any. */
  code?: string | null;
  /** Parsed response body, when available. */
  body?: unknown;
  /** True when the request timed out. */
  timeout?: boolean;
  /** True when the browser was offline. */
  offline?: boolean;
  /** True when a caller signal cancelled the request, not a real failure. */
  aborted?: boolean;
}

/**
 * Error thrown for any non-success gateway response or transport failure.
 * Carries enough structure for callers to branch without string matching.
 */
export class ApiError extends Error {
  /** HTTP status, or 0 for a transport failure. */
  status: number;
  /** Stable error code from the gateway, if any. */
  code: string | null;
  /** Parsed response body, when available. */
  body: unknown;
  /** True when the request timed out. */
  timeout: boolean;
  /** True when the browser was offline. */
  offline: boolean;
  /** True when a caller signal cancelled the request, not a real failure. */
  aborted: boolean;

  /**
   * @param message Human-readable summary.
   * @param opts Structured detail.
   */
  constructor(message: string, opts: ApiErrorOptions = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = opts.status ?? 0;
    this.code = opts.code ?? null;
    this.body = opts.body ?? null;
    this.timeout = opts.timeout ?? false;
    this.offline = opts.offline ?? false;
    this.aborted = opts.aborted ?? false;
  }

  /**
   * Whether retrying this error could plausibly succeed. Transport failures,
   * timeouts, and 5xx or 429 responses are retryable; 4xx are not.
   *
   * @returns True when a retry is worthwhile.
   */
  get retryable(): boolean {
    if (this.aborted) return false;
    if (this.timeout || this.offline) return true;
    if (this.status === 0) return true;
    if (this.status === 429) return true;
    return this.status >= 500 && this.status <= 599;
  }
}

/**
 * Hook that supplies the current bearer token. Wired by main.tsx to read from
 * the store, kept as a pluggable function so api.ts has no store-shape coupling
 * beyond the in-flight counter and so tests can inject a token.
 */
let getAuthToken: () => string | null = () => null;

/**
 * Install the auth-token provider.
 *
 * @param provider Returns the current bearer token or null when
 *   unauthenticated.
 */
export function setAuthTokenProvider(provider: () => string | null): void {
  getAuthToken = provider;
}

/** Non-secret local ownership of one authenticated browser session. */
export interface AuthRequestScope {
  /** Advances whenever login or local session ownership changes. */
  generation: number;
  /** Distinguishes an OAuth session from the legacy development-token path. */
  realSession: boolean;
  /** False when this tab no longer owns the shared browser session. */
  ownerCurrent: boolean;
}

/** Supplies the ownership boundary captured by one logical API request. */
let authRequestScopeProvider: (() => AuthRequestScope) | null = null;

/** Install or clear the provider used to bind retries and 401 replay. */
export function setAuthRequestScopeProvider(
  provider: (() => AuthRequestScope) | null,
): void {
  authRequestScopeProvider = provider;
}

/** Refuse an authenticated attempt after its originating session changed. */
function assertAuthRequestScope(expected: AuthRequestScope | null): void {
  if (!expected) return;
  const current = authRequestScopeProvider?.();
  if (
    !current ||
    current.generation !== expected.generation ||
    current.realSession !== expected.realSession ||
    !expected.ownerCurrent ||
    !current.ownerCurrent
  ) {
    throw new ApiError(
      'The authentication session changed. Retry from the active session.',
      { status: 401, code: 'SESSION_CHANGED' },
    );
  }
}

/**
 * Hook that proactively refreshes a near-expiry access token before an authed
 * request reads it. Wired by main.tsx to the auth module so api.ts has no static
 * dependency on the OAuth flow. Awaited before the token is attached on authed
 * requests; never called for unauthenticated requests so refresh cannot recurse.
 */
let tokenRefresher: (
  recovery: AuthRecoveryMode,
  signal?: AbortSignal,
) => Promise<void> = async () => {};

/**
 * Install the proactive token refresher.
 *
 * @param refresher Resolves once any needed refresh settles. Ordinary silent
 *   refresh failures are swallowed by the auth module; an explicitly permitted
 *   passkey recovery may reject to stop the request before it is sent.
 */
export function setTokenRefresher(
  refresher: (
    recovery: AuthRecoveryMode,
    signal?: AbortSignal,
  ) => Promise<void>,
): void {
  tokenRefresher = refresher;
}

/**
 * Hook that supplies the swap-identity token for a swap-service call. Wired by
 * main.tsx to the swap-token module so api.ts has no static dependency on it.
 * Awaited only for requests that opt in via the swapIdentity option, so ordinary
 * gateway calls never touch it. Returns the raw JWT to place in X-Swap-Identity;
 * a rejection propagates so a swap call fails closed rather than proceeding
 * without an identity token. Left unset it is a no-op that attaches no header, so
 * a swap call still works against a deployment that does not require the token.
 */
let swapIdentityProvider: ((signal?: AbortSignal) => Promise<string>) | null = null;

/**
 * Install the swap-identity token provider.
 *
 * @param provider Resolves the swap-identity JWT for a swap-service call, or null
 *   to attach no identity header.
 */
export function setSwapIdentityProvider(
  provider: ((signal?: AbortSignal) => Promise<string>) | null,
): void {
  swapIdentityProvider = provider;
}

/**
 * Hook that reactively refreshes after a 401, returning a fresh bearer token to
 * replay with or null when the session could not be refreshed. Wired by main.tsx
 * to the auth module.
 */
let unauthorizedHandler: (
  failedToken: string | null,
  context: AuthRecoveryContext,
) => Promise<string | null> = async () => null;

/**
 * Install the 401 handler.
 *
 * @param handler Returns a fresh token to replay with, or null to surface the
 *   401.
 */
export function set401Handler(
  handler: (
    failedToken: string | null,
    context: AuthRecoveryContext,
  ) => Promise<string | null>,
): void {
  unauthorizedHandler = handler;
}

/** Authentication recovery allowed for one logical request. */
export type AuthRecoveryMode = 'refresh-only' | 'passkey';

/** Context supplied to the reactive 401 handler. */
export interface AuthRecoveryContext {
  /** Recovery explicitly allowed by this request. */
  mode: AuthRecoveryMode;
  /** Caller cancellation that also governs any passkey recovery wait. */
  signal?: AbortSignal;
}

/** HTTP methods the request layer issues. */
export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/** A single query parameter value; undefined values are dropped. */
export type QueryValue = string | number | boolean | undefined;

/** Observable network boundary for one request attempt. */
export type RequestAttemptEvent =
  | { type: 'dispatched' }
  | { type: 'response'; status: number };

/** Options for {@link request}. */
export interface RequestOptions {
  /** HTTP method. Defaults to GET. */
  method?: HttpMethod;
  /** Query parameters; undefined values are dropped. */
  query?: Record<string, QueryValue>;
  /** JSON request body for write methods. */
  body?: unknown;
  /**
   * Extra request headers to attach, merged after the standard ones. Used by the
   * approve call to carry the device-assertion stamp. Values are attached as
   * given; a header the layer already sets, such as Authorization, is not meant
   * to be overridden here.
   */
  headers?: Record<string, string>;
  /**
   * Attach the Authorization header and run the proactive token refresh before
   * the request. Default true. The authentication endpoints (init, login,
   * refresh, logout-as-unauthenticated) pass false so the refresh flow cannot
   * recurse into itself.
   */
  auth?: boolean;
  /**
   * Recovery allowed after silent refresh is no longer possible. The default is
   * refresh-only so background reads and irreversible writes never open a
   * surprise WebAuthn prompt. Explicit pre-signing actions opt in to passkey.
   */
  authRecovery?: AuthRecoveryMode;
  /** Allow automatic retry. Default true for GET. */
  retry?: boolean;
  /**
   * Cache mode. Reads that must never be stale (balances, anything sensitive)
   * pass 'no-store'. Default 'no-store' so nothing is cached unless a caller
   * opts in.
   */
  cache?: 'default' | 'no-store';
  /** Override the default request timeout. */
  timeoutMs?: number;
  /** Caller abort signal, merged with the timeout. */
  signal?: AbortSignal;
  /**
   * Internal mutation observer. A dispatched event means `fetch` is about to be
   * called; a response event means the browser received an HTTP status. Write
   * pipelines use this to distinguish a proven pre-dispatch failure from an
   * outcome that may already have committed server-side. A dispatched observer
   * may throw to fail closed before fetch (for example when durable mutation
   * coordination cannot be recorded); it is normalized without being mistaken
   * for a dispatched transport failure.
   */
  onRequestAttempt?: (event: RequestAttemptEvent) => void;
  /**
   * Resolve the request path against a base other than the gateway base. Swap
   * calls pass the swap-base resolver so they can target a separately fronted
   * swap service; omitted for ordinary gateway calls, which resolve against
   * `apiUrl`. The resolver receives the path and returns the absolute URL.
   */
  resolveUrl?: (path: string) => string;
  /**
   * Attach the swap-identity token as the X-Swap-Identity header. Only the
   * swap-service calls set this. The token is minted by the gateway and rides in
   * its own header rather than Authorization, which the reverse proxy overwrites
   * with a service bearer. When the provider is unset this is a no-op and no
   * header is attached, so a swap call still works where the token is not
   * required; when the provider rejects, the request fails closed rather than
   * calling the swap service without an identity token.
   */
  swapIdentity?: boolean;
}

/**
 * Perform a request and return the unwrapped `data` payload.
 *
 * @param path API path beginning with a slash.
 * @param options Request options.
 * @returns The parsed `data` field of the response envelope.
 * @throws {ApiError} On any non-success status or transport failure.
 */
export async function request<T = unknown>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';
  const useAuth = options.auth ?? true;
  const allowRetry = options.retry ?? method === 'GET';
  const maxAttempts = allowRetry ? config.maxRetries + 1 : 1;
  const authScope = useAuth ? authRequestScopeProvider?.() ?? null : null;

  let lastError: ApiError | null = null;
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    if (attempt > 0) {
      // Exponential backoff with jitter so retries do not thunder.
      const base = 200 * 2 ** (attempt - 1);
      const jitter = Math.floor(Math.random() * 100);
      await delay(base + jitter);
    }
    try {
      return await once<T>(path, method, options, authScope);
    } catch (err) {
      const code =
        err &&
        typeof err === 'object' &&
        'code' in err &&
        typeof err.code === 'string'
          ? err.code
          : undefined;
      const apiErr =
        err instanceof ApiError
          ? err
          : new ApiError(err instanceof Error ? err.message : String(err), { code });
      lastError = apiErr;
      if (!allowRetry || !apiErr.retryable) throw apiErr;
    }
  }
  throw lastError ?? new ApiError('request failed');
}

/**
 * Execute exactly one attempt of a request.
 *
 * @param path API path.
 * @param method HTTP method.
 * @param options Request options.
 * @param replayed True when this is the post-refresh replay of a 401.
 * @returns The unwrapped payload.
 */
async function once<T>(
  path: string,
  method: HttpMethod,
  options: RequestOptions,
  authScope: AuthRequestScope | null,
  replayed = false,
): Promise<T> {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    throw new ApiError('browser is offline', { offline: true });
  }

  const resolve = options.resolveUrl ?? apiUrl;
  const url = withQuery(resolve(path), options.query);
  const headers = new Headers({ accept: 'application/json' });

  const useAuth = options.auth ?? true;
  const authRecovery = options.authRecovery ?? 'refresh-only';
  let attemptedToken: string | null = null;
  if (useAuth) {
    // Proactively rotate a near-expiry token before attaching it, so an authed
    // read carries a token with headroom. Skipped for unauthenticated requests,
    // which is what keeps the refresh endpoint itself from recursing into a
    // refresh. An explicitly permitted passkey recovery may reject here to stop
    // the request before it is sent. Ordinary refresh transport failures are
    // swallowed, while missing safe cross-tab coordination fails closed here.
    // Let the auth owner seam run before the first scope assertion. If a peer
    // replaced a resumed tab before this request began, the refresher performs
    // the required local-only stale-session cleanup; the assertion immediately
    // below still prevents this logical request from crossing into a successor.
    if (!replayed) await tokenRefresher(authRecovery, options.signal);
    assertAuthRequestScope(authScope);
    attemptedToken = getAuthToken();
    if (!attemptedToken) {
      throw new ApiError('Authentication is required.', {
        status: 401,
        code: 'UNAUTHENTICATED',
      });
    }
    headers.set('authorization', `Bearer ${attemptedToken}`);
  }

  // Swap-service calls carry a per-user identity token in a dedicated header so
  // the swap service can scope its data to the caller's org. It is deliberately
  // not the Authorization header, which the reverse proxy overwrites with a
  // service-to-service bearer. When no provider is installed no header is
  // attached; when the provider rejects the request is abandoned so the swap
  // call fails closed rather than reaching the swap service unscoped.
  if (options.swapIdentity && swapIdentityProvider) {
    const identity = await swapIdentityProvider(options.signal);
    assertAuthRequestScope(authScope);
    if (identity) headers.set('X-Swap-Identity', identity);
  }

  // Caller-supplied headers are attached last, for example the device-assertion
  // stamp on an approve. They are additive; the layer's own headers above take
  // precedence for the names it manages.
  if (options.headers) {
    for (const [name, value] of Object.entries(options.headers)) {
      headers.set(name, value);
    }
  }

  const init: RequestInit = {
    method,
    headers,
    // Default to no-store so neither the HTTP cache nor the service worker can
    // serve a stale balance or a sensitive response. Callers opt in to caching.
    cache: options.cache ?? 'no-store',
    credentials: 'omit',
    mode: 'cors',
  };

  if (options.body !== undefined) {
    headers.set('content-type', 'application/json');
    init.body = JSON.stringify(options.body);
  }

  const timeoutMs = options.timeoutMs ?? config.requestTimeoutMs;
  const controller = new AbortController();
  init.signal = mergeSignals(controller.signal, options.signal);

  assertAuthRequestScope(authScope);
  if (init.signal.aborted) {
    throw new ApiError('request was cancelled', { status: 0, aborted: true });
  }
  const timer = setTimeout(() => controller.abort(new DOMException('timeout', 'AbortError')), timeoutMs);
  adjustInFlight(1);
  let response: Response;
  try {
    options.onRequestAttempt?.({ type: 'dispatched' });
  } catch (err) {
    clearTimeout(timer);
    adjustInFlight(-1);
    throw err;
  }
  try {
    response = await fetch(url, init);
    options.onRequestAttempt?.({ type: 'response', status: response.status });
  } catch (err) {
    clearTimeout(timer);
    adjustInFlight(-1);
    assertAuthRequestScope(authScope);
    const aborted = err instanceof DOMException && err.name === 'AbortError';
    if (aborted) {
      // The timeout controller firing means a real timeout. Otherwise the
      // caller's own signal aborted the request, for example a component
      // unmounting or an effect re-running cancelled the read. That is a benign
      // cancellation, flagged so callers can ignore it rather than surface a
      // spurious network error.
      if (controller.signal.aborted) throw new ApiError('request timed out', { timeout: true });
      throw new ApiError('request was cancelled', { status: 0, aborted: true });
    }
    throw new ApiError('network request failed', { status: 0 });
  }
  clearTimeout(timer);
  adjustInFlight(-1);

  const payload = await parseBody(response);
  assertAuthRequestScope(authScope);

  if (!response.ok) {
    // 503 is the maintenance gate on the host (Caddy answers /v1/* directly
    // while MAINTENANCE_MODE=1). Recorded BEFORE the 401 replay below: during a
    // window every request fails, and a 503 must not be mistaken for an expired
    // session and burn a refresh attempt. Callers still get their ApiError, so
    // no existing error handling changes; the shell reads the flag separately.
    if (response.status === 503) {
      reportMaintenance();
    }
    // A 401 on an authed request gets one reactive refresh-and-replay. The
    // handler rotates the token under its own single-flight guard and returns a
    // fresh one, or null when the session is gone. Guarded by `replayed` so the
    // replay cannot itself trigger another refresh and loop.
    if (response.status === 401 && useAuth && !replayed) {
      const refreshed = await unauthorizedHandler(attemptedToken, {
        mode: authRecovery,
        signal: options.signal,
      });
      assertAuthRequestScope(authScope);
      if (refreshed) {
        return once<T>(path, method, options, authScope, true);
      }
    }
    const detail = extractError(payload);
    throw new ApiError(detail.message || `request failed with ${response.status}`, {
      status: response.status,
      code: detail.code,
      body: payload,
    });
  }

  // The gateway wraps successful payloads in `{ data: ... }`. Unwrap when
  // present, otherwise return the body as-is for endpoints that do not wrap.
  if (payload && typeof payload === 'object' && 'data' in payload) {
    return (payload as { data: T }).data;
  }
  return payload as T;
}

/**
 * Parse a response body as JSON, tolerating empty and non-JSON bodies.
 *
 * @param response The fetch response.
 * @returns Parsed JSON, or null for an empty body.
 */
async function parseBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

/** Normalized error detail pulled out of a gateway error body. */
interface ErrorDetail {
  message: string;
  code: string | null;
}

/**
 * Pull a message and code out of a gateway error body. The gateway error shape
 * is `{ error: { code, message } }` or a bare string; both are handled.
 *
 * @param payload Parsed error body.
 * @returns Normalized error detail.
 */
function extractError(payload: unknown): ErrorDetail {
  if (payload && typeof payload === 'object') {
    const obj = payload as Record<string, unknown>;
    if (obj.error && typeof obj.error === 'object') {
      const err = obj.error as Record<string, unknown>;
      return {
        message: String(err.message ?? ''),
        code: err.code != null ? String(err.code) : null,
      };
    }
    if (typeof obj.message === 'string') {
      return { message: obj.message, code: obj.code != null ? String(obj.code) : null };
    }
  }
  if (typeof payload === 'string') return { message: payload, code: null };
  return { message: '', code: null };
}

/**
 * Append query parameters to a URL, dropping undefined values.
 *
 * @param url Base URL.
 * @param query Parameters.
 * @returns URL with an encoded query string when parameters exist.
 */
function withQuery(url: string, query?: Record<string, QueryValue>): string {
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined) continue;
    params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

/**
 * Merge a timeout signal with an optional caller signal so either can abort.
 *
 * @param primary The timeout signal.
 * @param secondary An optional caller-supplied signal.
 * @returns A signal aborted when either input aborts.
 */
function mergeSignals(primary: AbortSignal, secondary?: AbortSignal): AbortSignal {
  if (!secondary) return primary;
  if (typeof AbortSignal !== 'undefined' && typeof AbortSignal.any === 'function') {
    return AbortSignal.any([primary, secondary]);
  }

  const caller = secondary;
  const controller = new AbortController();
  function cleanup(): void {
    primary.removeEventListener('abort', onPrimaryAbort);
    caller.removeEventListener('abort', onSecondaryAbort);
  }
  function abortFrom(signal: AbortSignal): void {
    if (controller.signal.aborted) return;
    cleanup();
    controller.abort(signal.reason);
  }
  function onPrimaryAbort(): void {
    abortFrom(primary);
  }
  function onSecondaryAbort(): void {
    abortFrom(caller);
  }

  // Native AbortSignal.any() returns an already-aborted composite when an
  // input was cancelled before composition. Match that behavior so a caller
  // abort that fires during authentication preflight cannot be lost.
  if (primary.aborted) {
    abortFrom(primary);
    return controller.signal;
  }
  if (caller.aborted) {
    abortFrom(caller);
    return controller.signal;
  }

  primary.addEventListener('abort', onPrimaryAbort, { once: true });
  caller.addEventListener('abort', onSecondaryAbort, { once: true });

  // Recheck after listener registration so even non-standard signal sources
  // cannot create a check-to-listen gap.
  if (primary.aborted) onPrimaryAbort();
  else if (caller.aborted) onSecondaryAbort();

  return controller.signal;
}

/**
 * Resolve after a delay.
 *
 * @param ms Milliseconds to wait.
 * @returns Resolves when the delay elapses.
 */
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Options carrying only an abort signal, shared by the typed helpers. */
export interface SignalOptions {
  /** Abort signal. */
  signal?: AbortSignal;
}

/** Internal lifecycle observer used by non-idempotent typed write helpers. */
export interface RequestAttemptObserverOptions {
  /** Observe whether an attempt was dispatched and which response arrived. */
  onRequestAttempt?: (event: RequestAttemptEvent) => void;
}

/** Options for safe calls that may explicitly recover a passkey session. */
export interface AuthRecoverySignalOptions extends SignalOptions {
  /** Authentication recovery explicitly allowed for this call. */
  authRecovery?: AuthRecoveryMode;
}

/** Options for transaction creation, including its non-idempotent boundary. */
export interface TransactionMutationOptions
  extends AuthRecoverySignalOptions,
    RequestAttemptObserverOptions {}

/* Typed GET helpers the view and service layers consume. */

/**
 * List the account's wallets.
 *
 * @param opts Options.
 * @returns Wallet summaries. The raw gateway shape is mapped to the store's
 *   WalletSummary by the caller in services/wallets; this helper returns the
 *   gateway records verbatim.
 */
export function getWallets(opts: SignalOptions = {}): Promise<unknown> {
  return request('/v1/wallets', { method: 'GET', signal: opts.signal });
}

/**
 * Fetch a single wallet by id.
 *
 * @param walletId Wallet uuid.
 * @param opts Options.
 * @returns The gateway WalletResponse record.
 */
export function getWallet(walletId: string, opts: SignalOptions = {}): Promise<unknown> {
  return request(`/v1/wallets/${encodeURIComponent(walletId)}`, {
    method: 'GET',
    signal: opts.signal,
  });
}

/** Options for {@link getWalletBalances}. */
export interface BalancesOptions extends SignalOptions {
  /** Optional chain override the gateway honors. */
  chain?: string;
  /**
   * Imported custom-token contract addresses to append to the response, so an
   * imported token's live balance refreshes on the same poll as the curated ones.
   * The gateway resolves and appends each as an extra unverified entry with a null
   * fiat, and caps the number it processes, so a long list is bounded server-side.
   * Passed as a comma-separated query value the gateway splits.
   */
  imports?: readonly string[];
}

/**
 * Fetch the assembled balances for one wallet. Always network-only: balances
 * must never be served from any cache.
 *
 * @param walletId Wallet uuid.
 * @param opts Options.
 * @returns The gateway WalletBalancesResponse record.
 */
export function getWalletBalances(walletId: string, opts: BalancesOptions = {}): Promise<Record<string, unknown>> {
  // The imports are sent as a single comma-separated value; an empty list is
  // omitted so an ordinary read carries no imports parameter at all.
  const imports = opts.imports && opts.imports.length > 0 ? opts.imports.join(',') : undefined;
  return request(`/v1/wallets/${encodeURIComponent(walletId)}/balances`, {
    method: 'GET',
    query: { chain: opts.chain, imports },
    cache: 'no-store',
    signal: opts.signal,
  });
}

/** Fields sent to resolve an imported custom token. */
export interface ResolveImportedTokenBody {
  /** The chain family key, for example "evm", "tron", "solana", or "sui". */
  chain: string;
  /** The concrete network key for a multi-network wallet, or undefined. */
  network?: string;
  /** The token contract address to resolve. */
  contract_address: string;
}

/**
 * Resolve an imported custom token's metadata and balance for one wallet. The
 * gateway derives the owner address server-side from the wallet, reads the token's
 * name, symbol, and decimals plus the wallet's balance of it, and returns them
 * with verified false and a null fiat so an imported token never inflates a total.
 * The wallet is resolved under the same tenant scoping as the balances read, so a
 * foreign wallet is a not-found rather than a forbidden. A read that touches no
 * signing path, so it may retry on a transient failure like the other reads.
 *
 * @param walletId Wallet uuid the token is resolved against.
 * @param body The chain, optional network, and contract address to resolve.
 * @param opts Options.
 * @returns The gateway's resolved-token record.
 */
export function resolveImportedToken(
  walletId: string,
  body: ResolveImportedTokenBody,
  opts: SignalOptions = {},
): Promise<Record<string, unknown>> {
  return request(`/v1/wallets/${encodeURIComponent(walletId)}/tokens/resolve`, {
    method: 'POST',
    body,
    // A metadata read, not a fund-moving write, so a transient failure may retry.
    retry: true,
    cache: 'no-store',
    signal: opts.signal,
  });
}

/** The EIP-1559 fee readings the gateway priced for one exact transfer. */
export interface EvmFees {
  /** The EVM chain id the fees were priced for, echoed by the gateway. */
  chainId: number;
  /** The wallet's own EVM address the estimate was run from, derived
   * server-side from the stored public key and never supplied by the client. */
  address: string;
  /** Next block's base fee, in wei, base-10. What the ceiling was derived from. */
  baseFeePerGas: string;
  /** Suggested priority fee (tip), in wei, base-10. */
  maxPriorityFeePerGas: string;
  /** Suggested maxFeePerGas, in wei, base-10, already checked against the ceiling. */
  maxFeePerGas: string;
  /** Gas estimated for this exact transfer, base-10. */
  gasLimit: string;
}

/** The raw gateway response before normalization to {@link EvmFees}. */
interface GatewayEvmFeesResponse {
  chain_id: number;
  address: string;
  base_fee_per_gas: string;
  max_priority_fee_per_gas: string;
  max_fee_per_gas: string;
  gas_limit: string;
}

/** What {@link getWalletEvmFees} prices. */
export interface EvmFeesQuery {
  /** The gateway registry chain key to price on, e.g. "ethereum" or "base". */
  chain: string;
  /** The intended recipient. Required, because the gas limit comes from an
   * eth_estimateGas of this exact transfer: a contract recipient costs more
   * than an EOA, so pricing a different destination strands the transaction. */
  to: string;
  /** The amount in wei, base-10, matching the balance encoding. */
  value: string;
}

/**
 * Price one native EVM transfer so the client can fill the fee fields of an
 * EIP-1559 envelope before it is signed.
 *
 * The browser cannot take this reading itself — the app's Content-Security-Policy
 * is connect-src 'self', so it cannot reach a JSON-RPC — which is why EVM send
 * was gated to the native app for as long as nothing same-origin could answer
 * for the chain. This is the gateway's own answer, and it sits beside the
 * evm-nonce read that supplies the other scalar the envelope needs.
 *
 * Preferred over the swap service's cross-chain gas estimator for a plain send.
 * Both read the same live values, but this one is purpose-built for the transfer
 * path: it derives the FROM address server-side from the stored public key (so a
 * caller can never price from an address it does not own), enforces the served
 * tier, and refuses a non-EVM chain or an unfinalized key at the trust boundary.
 * It also fails closed where the swap path does not — the swap estimator carries
 * a DEFAULT_MAX_FEE_WEI fallback, which is right for a quote and wrong for a
 * transfer, because a guessed fee on a congested chain produces a transaction
 * that never mines.
 *
 * A deployment whose gateway predates this endpoint answers 404, which resolves
 * to null so the caller can refuse with a clear "not enabled here" message
 * rather than signing against a fee it never read — the same posture, and the
 * same reasoning, as the nonce read in lib/swapApi.
 *
 * @param walletId The wallet that will sign and pay; its address is resolved
 *   server-side and scoped to the caller's tenant, so a foreign wallet is a
 *   not-found rather than a forbidden.
 * @param query The exact transfer to price.
 * @param opts Options; the abort signal cancels the in-flight read.
 * @returns The fee readings, or null when the endpoint is not deployed.
 */
export async function getWalletEvmFees(
  walletId: string,
  query: EvmFeesQuery,
  opts: AuthRecoverySignalOptions = {},
): Promise<EvmFees | null> {
  try {
    const res = await request<GatewayEvmFeesResponse>(
      `/v1/wallets/${encodeURIComponent(walletId)}/evm-fees`,
      {
        method: 'GET',
        query: { chain: query.chain, to: query.to, value: query.value },
        // A read that moves nothing, so a transient failure may retry.
        retry: true,
        // Never cache a fee: a stale reading underprices a congested chain and
        // produces a transaction that never mines.
        cache: 'no-store',
        signal: opts.signal,
        authRecovery: opts.authRecovery,
      },
    );
    return {
      chainId: res.chain_id,
      address: res.address,
      baseFeePerGas: res.base_fee_per_gas,
      maxPriorityFeePerGas: res.max_priority_fee_per_gas,
      maxFeePerGas: res.max_fee_per_gas,
      gasLimit: res.gas_limit,
    };
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

/**
 * One NFT item as the gateway serializes it. The token id and quantity are
 * base-10 strings and must stay strings end to end: a uint256 token id does not
 * fit a JS number. The image_url is always a same-origin path the gateway proxies
 * (for example /v1/nft-media/<hex>), so it renders as-is under the strict
 * img-src 'self' content security policy and is never rewritten or replaced with
 * an external URL.
 */
export interface NftItemView {
  /** Stored-import uuid, or null for an item read live from the chain. */
  id: string | null;
  /** Chain key the item lives on. */
  chain: string;
  /** Contract address, or the object id for a Sui item. */
  contract: string;
  /** Token id within the contract, as a base-10 string. */
  token_id: string;
  /** Token standard, for example "erc721", "erc1155", "trc721", "sui-object". */
  standard: string;
  /** Held quantity as a base-10 string; above one only for erc1155. */
  quantity: string;
  /** Item name, or null when the metadata carries none. */
  name: string | null;
  /** Collection name, or null. */
  collection_name: string | null;
  /** Collection symbol, or null. */
  symbol: string | null;
  /** Same-origin media path, or null when no image is known. */
  image_url: string | null;
  /** The raw token metadata URI, or null. */
  token_uri: string | null;
  /** Whether the user hid this stored item from the gallery. */
  hidden: boolean;
  /** How the item was recorded: "manual", "discovered", or "chain". */
  source: string;
  /** Unix seconds ownership was last verified, or null. */
  last_verified_at: number | null;
}

/** One page of a wallet's NFTs as the gateway serializes it. */
export interface WalletNftsPage {
  /** Wallet the page describes. */
  wallet_id: string;
  /** Chain key the wallet acts on. */
  chain: string;
  /** The NFT items on this page. */
  items: NftItemView[];
  /** Opaque cursor for the next page, or null on the last page. */
  next_cursor: string | null;
  /**
   * A user-facing note, set with an empty items list when the wallet's chain does
   * not support NFTs yet (for example Solana or Bitcoin), or null.
   */
  note: string | null;
}

/**
 * Fetch one page of a wallet's NFTs. Network-only like the balances read, so a
 * hide or an import is never masked by a cached page.
 *
 * @param walletId Wallet uuid.
 * @param cursor Opaque cursor from a previous page, or undefined for the first.
 * @param opts Options.
 * @returns One page of NFT items plus the cursor for the next page.
 */
export function getWalletNfts(
  walletId: string,
  cursor?: string,
  opts: SignalOptions = {},
): Promise<WalletNftsPage> {
  return request(`/v1/wallets/${encodeURIComponent(walletId)}/nfts`, {
    method: 'GET',
    query: { cursor },
    cache: 'no-store',
    signal: opts.signal,
  });
}

/** Fields sent to resolve an NFT against a wallet. */
export interface ResolveNftBody {
  /** The chain family key, for example "evm", "tron", or "sui". */
  chain: string;
  /** The concrete network key for a unified EVM wallet, or undefined. */
  network?: string;
  /** The NFT contract address, or the object id for a Sui item. */
  contract: string;
  /** The token id within the contract; required for EVM and Tron, absent for Sui. */
  token_id?: string;
}

/**
 * Resolve and import an NFT for one wallet. The gateway verifies ownership
 * server-side and records the item, so a 400 carries an actionable message (for
 * example that the wallet does not hold the token) the sheet shows inline. A
 * verification read that touches no signing path, so it may retry on a transient
 * failure like the token resolve.
 *
 * @param walletId Wallet uuid the NFT is resolved against.
 * @param body The chain, optional network, contract, and optional token id.
 * @param opts Options.
 * @returns The resolved NFT item record.
 */
export function resolveWalletNft(
  walletId: string,
  body: ResolveNftBody,
  opts: SignalOptions = {},
): Promise<NftItemView> {
  return request(`/v1/wallets/${encodeURIComponent(walletId)}/nfts/resolve`, {
    method: 'POST',
    body,
    retry: true,
    cache: 'no-store',
    signal: opts.signal,
  });
}

/**
 * Hide or show one stored NFT item in a wallet's gallery. Only a stored import
 * (an item with a non-null id) can be hidden; a live chain item has no record to
 * flag. A state-changing write, so it never auto-retries.
 *
 * @param walletId Wallet uuid the item belongs to.
 * @param itemId The stored item's uuid.
 * @param hidden True to hide the item, false to show it again.
 * @param opts Options.
 * @returns The item id and its new hidden flag.
 */
export function setNftHidden(
  walletId: string,
  itemId: string,
  hidden: boolean,
  opts: SignalOptions = {},
): Promise<{ id: string; hidden: boolean }> {
  return request(
    `/v1/wallets/${encodeURIComponent(walletId)}/nfts/${encodeURIComponent(itemId)}/hidden`,
    {
      method: 'POST',
      body: { hidden },
      cache: 'no-store',
      signal: opts.signal,
    },
  );
}

/** Options for {@link getTransactions}. */
export interface TransactionsOptions extends SignalOptions {
  /** Restrict to one wallet. */
  walletId?: string;
  /** Page size. */
  limit?: number;
  /** Keyset cursor from a previous page. */
  cursor?: string;
}

/**
 * One page of transaction history as the gateway serializes it. The gateway's
 * TxPage carries the rows under `items` (newest first) plus an opaque `next_cursor`
 * for the following page, or null on the last page. The response envelope
 * `{ data: TxPage }` is unwrapped by the transport, so this is the shape callers
 * receive directly.
 */
export interface TxPage {
  /** The transaction rows on this page, newest first. */
  items: Record<string, unknown>[];
  /** Opaque keyset cursor for the next page, or null when this is the last page. */
  next_cursor: string | null;
}

/**
 * Fetch one page of transaction history.
 *
 * @param opts Options.
 * @returns A page of transaction records plus the cursor for the next page.
 */
export function getTransactions(opts: TransactionsOptions = {}): Promise<TxPage> {
  return request('/v1/transactions', {
    method: 'GET',
    query: { wallet_id: opts.walletId, limit: opts.limit, cursor: opts.cursor },
    signal: opts.signal,
  });
}

/**
 * Fetch the deployment config, including the active network environment. This
 * endpoint is unauthenticated and inert, so it may be cached briefly.
 *
 * @param opts Options.
 * @returns The deployment config record.
 */
export function getDeploymentConfig(opts: SignalOptions = {}): Promise<unknown> {
  return request('/v1/config', {
    method: 'GET',
    auth: false,
    cache: 'default',
    signal: opts.signal,
  });
}

/* Typed write helpers. Writes never auto-retry: a fund-moving or state-changing
   call must not be silently replayed by the transport. The single reactive
   401 refresh-and-replay in once() is still safe, because it only re-issues
   after the original request was rejected for an expired token and a fresh one
   was minted; ordinary network-blip retry is what these turn off. */

/**
 * Create a wallet. The gateway runs the DKG ceremony synchronously before it
 * responds, so this resolves only once the wallet is active and carries its
 * combined public key. The longer timeout reflects that blocking ceremony and
 * applies to this call alone; ordinary reads keep the default request timeout.
 *
 * @param body CreateWalletRequest fields: { label, threshold, num_parties,
 *   curve?, chain? }. No genesis field is sent: the account-custody flow creates
 *   self-custody wallets.
 * @param opts Options.
 * @returns The gateway WalletResponse record.
 */
export function createWallet(body: unknown, opts: SignalOptions = {}): Promise<Record<string, unknown>> {
  return request('/v1/wallets', {
    method: 'POST',
    body,
    retry: false,
    timeoutMs: 120000,
    signal: opts.signal,
  });
}

/**
 * Ensure the account holds the standard wallet set, one per supported chain.
 *
 * Returns as soon as the gateway has planned the work, NOT when the wallets
 * exist: each missing chain is dispatched to a background ceremony and the
 * response is a plan of per-chain statuses. Poll {@link listWallets} to watch
 * them appear. This request uses the automatic set's seven-second budget;
 * manual {@link createWallet} retains its separate ceremony timeout.
 *
 * Safe to call repeatedly, and meant to be: the gateway derives what is missing
 * from the wallets that exist rather than from a job record, so a retry resumes
 * a partial run instead of duplicating it. That is the recovery path for a run
 * cut short by a closed tab, a dropped connection, or a restart.
 *
 * `retry: false` regardless, because the transport's blind retry would fire a
 * second provision before the first has finished planning; the caller retries
 * deliberately instead.
 *
 * @param opts Options.
 * @returns Per-chain plan entries: `{ chain, status }` where status is
 *   `existing` (a wallet was already there), `starting` (a ceremony was
 *   dispatched), or `unavailable` (no key for that chain is served by this
 *   deployment, so it will never appear — do not poll for it).
 */
export function provisionWallets(opts: SignalOptions = {}): Promise<unknown> {
  return request('/v1/wallets/provision', {
    method: 'POST',
    retry: false,
    timeoutMs: AUTO_PROVISION_TIMEOUT_MS,
    signal: opts.signal,
  });
}

/**
 * Submit a transaction. This does not sign on its own: the gateway evaluates
 * policy, creates the transaction record, and opens the matching approval.
 * Reaching the approval quorum is what dispatches the server-side MPC signing
 * and broadcast. Never auto-retried, so a fund-moving submit is not silently
 * replayed by the transport.
 *
 * @param body CreateTransactionRequest fields: { wallet_id, destination, amount,
 *   asset, chain, note? }. The supported web chains build their transaction
 *   server-side, so no unsigned_payload is sent.
 * @param opts Options.
 * @returns The gateway TransactionResponse record.
 */
export function createTransaction(
  body: unknown,
  opts: TransactionMutationOptions = {},
): Promise<unknown> {
  return request('/v1/transactions', {
    method: 'POST',
    body,
    retry: false,
    signal: opts.signal,
    authRecovery: opts.authRecovery,
    onRequestAttempt: opts.onRequestAttempt,
  });
}

/**
 * Fetch one transaction by id, used to poll status after a submit. A read, so it
 * may retry on a transient failure.
 *
 * @param txId Transaction uuid.
 * @param opts Options.
 * @returns The gateway TransactionResponse record.
 */
export function getTransaction(txId: string, opts: SignalOptions = {}): Promise<unknown> {
  return request(`/v1/transactions/${encodeURIComponent(txId)}`, {
    method: 'GET',
    signal: opts.signal,
  });
}

/**
 * List the org's approval records. Used to find the approval the gateway opened
 * for a just-created transaction so it can be approved.
 *
 * @param opts Options.
 * @returns Approval records.
 */
export function getApprovals(opts: AuthRecoverySignalOptions = {}): Promise<unknown[]> {
  return request('/v1/approvals', {
    method: 'GET',
    signal: opts.signal,
    authRecovery: opts.authRecovery,
  });
}

/**
 * Approve an approval, which drives the gateway's server-side MPC sign and
 * broadcast once the quorum is met. For a single-approver self-custody wallet
 * the creating user may approve their own transaction. Never auto-retried.
 *
 * The request body is an ApprovalActionRequest. Today it carries only the
 * optional reason, so the self-custody cohort sends { reason? } or an empty
 * object. It also accepts an optional approval_signature for hardened,
 * genesis-created wallets, populated by the caller when present. The engine
 * verifies that signature against a canonical decision digest built from the
 * approver's compressed secp256k1 public key (33 bytes), the compact ECDSA
 * signature as r||s with low-s normalization (64 bytes), the decision byte (u8),
 * and the factor id hint. This transport helper is agnostic to the body shape:
 * populating the signature later is a pure field addition, not a change here.
 *
 * @param approvalId Approval uuid.
 * @param body ApprovalActionRequest fields: { reason?, approval_signature? }. The
 *   single-approver self-custody flow omits the cryptographic approval signature,
 *   so the body stays the bare optional reason.
 * @param opts Options. The optional headers carry the device-assertion stamp
 *   (X-Device-Assertion) when the caller has an enrolled passkey; omitted, the
 *   approve behaves exactly as before.
 * @returns The updated approval record.
 */
export function approveApproval(
  approvalId: string,
  body: unknown = {},
  opts: SignalOptions & RequestAttemptObserverOptions & {
    headers?: Record<string, string>;
  } = {},
): Promise<unknown> {
  return request(`/v1/approvals/${encodeURIComponent(approvalId)}/approve`, {
    method: 'POST',
    body,
    retry: false,
    // An approve that completes the quorum runs the MPC sign and broadcast
    // inline before the gateway responds, a multi-second call, so this write
    // gets the same long per-request timeout the native clients use rather than
    // the short global default.
    timeoutMs: 120000,
    headers: opts.headers,
    signal: opts.signal,
    onRequestAttempt: opts.onRequestAttempt,
  });
}

/**
 * Reject an approval, which prevents the gateway from signing and broadcasting
 * the underlying transaction. Mirrors {@link approveApproval}: the endpoint takes
 * the same optional ApprovalActionRequest body, and the write is never
 * auto-retried so a fund-affecting decision is not silently replayed. The
 * gateway remains the authority; this only relays the human decision.
 *
 * @param approvalId Approval uuid.
 * @param body ApprovalActionRequest fields: { reason? }.
 * @param opts Options.
 * @returns The updated approval record.
 */
export function rejectApproval(
  approvalId: string,
  body: unknown = {},
  opts: SignalOptions & RequestAttemptObserverOptions = {},
): Promise<unknown> {
  return request(`/v1/approvals/${encodeURIComponent(approvalId)}/reject`, {
    method: 'POST',
    body,
    retry: false,
    // Matches the approve call's long per-request timeout so a slow decision
    // write is not cut off by the short global default mid-flight.
    timeoutMs: 120000,
    signal: opts.signal,
    onRequestAttempt: opts.onRequestAttempt,
  });
}
